import classNames from "classnames";
import React from "react";
import { useTranslation } from "react-i18next";

import style from "./side-panel-logs.scss";
import SidePanelDatePicker from "./SidePanelDatePicker";
import { SubworkflowLogsModal } from "./SubworkflowLogsModal";
import Checkmark from "components/icons/Checkmark";
import Exit from "components/icons/Exit";
import RefreshIcon from "components/icons/RefreshIcon";
import { Reports } from "components/icons/Reports";
import RestartIcon from "components/icons/RestartIcon";
import RunningIcon from "components/icons/RunningIcon";
import ScheduledIcon from "components/icons/ScheduledIcon";
import SubworkflowLogs from "components/icons/SubworkflowLogs";
import Warning from "components/icons/Warning";
import { LoadingIndicator } from "components/loading-indicator/LoadingIndicator";
import { DownloadIcon } from "components/products/home-icons/DownloadIcon";
import SearchView from "components/search/SearchView";
import Tooltip from "components/tooltip/Tooltip";
import { AUTH_WORKFLOW_SECURED, AUTH_WORKFLOW_TEMPLATE_SECURED } from "domain/authority";
import { FilterState, LaunchType, WorkflowEntityType } from "domain/workflows";
import { Action, Category, Label, usageStatisticsService } from "services/statistics/UsageStatisticsService";
import { isCurrentUserInternal } from "services/tenants/tenantCookieService";
import { userSessionService } from "services/user/UserSessionService";
import { templateService } from "services/workflows/TemplateService";
import {
    FailureCode,
    getReportViewUrl,
    State,
    workflowService,
    WorkflowStateDetails,
} from "services/workflows/WorkflowService";
import buttons from "styles/buttons.scss";
import formStyle from "styles/form.scss";
import { formatTimestamp } from "utils/format";
import { useTheme } from "utils/useTheme";

import failedIcon from "assets/images/icons/failed.svg";
import warningIcon from "assets/images/icons/warning.svg";

import testIds from "testIds.json";

interface SidePanelLogsProps {
    showSearch: boolean;
    showDateFilter: boolean;
    setShowDateFilter: (show: boolean) => void;
    setShowSearch: (show: boolean) => void;
    filter: State;
    workflowUuid: string;
    testMode: boolean;
    filterState?: FilterState;
    setFilterState?: React.Dispatch<React.SetStateAction<FilterState>>;
    autoRefresh: boolean;
    setAutoRefresh: (value: boolean) => void;
    refreshCooldown: boolean;
    setRefreshCooldown: (value: boolean) => void;
    enabled?: boolean;
    isTemplate?: boolean;
    launchType?: LaunchType;
    secured?: boolean;
}

interface TableState {
    logs: WorkflowStateDetails[];
    cursor: string | null;
    count: number;
}

interface TranslationOptions {
    count?: number;

    [key: string]: unknown;
}

const formatDate = (dateString: string) => {
    const isTimeFirst = false;
    const convertToLocal = true;
    return formatTimestamp(dateString, isTimeFirst, convertToLocal);
};

export const CHECKMARK_SIZE = 18;
export const STROKE_WIDTH = 1.5;

const AUTO_REFRESH_INTERVAL_MS = 30000;
const REFRESH_COOLDOWN_MS = 5000;

export function getFailureInfo(
    failureCode: FailureCode | null,
    t: (key: string, options?: { [key: string]: unknown }) => string,
    theme: { errorTextColor: string },
    entityType: string
): {
    styleClass: string;
    text: string;
    showDate: boolean;
    iconComponent: JSX.Element;
} {
    switch (failureCode) {
        case FailureCode.QUOTA_EXCEEDED:
            return {
                styleClass: style.failedStatusContent,
                text: t("WorkflowsTable.sidePanel.quotaLimitReached"),
                showDate: false,
                iconComponent: <img src={warningIcon} alt="warning" className={style.warningIcon} />,
            };
        case FailureCode.UNKNOWN_ERROR:
            return {
                styleClass: style.failedStatusContent,
                text: t("WorkflowsTable.sidePanel.unexpectedErrorRestart"),
                showDate: true,
                iconComponent: <Warning color={theme.errorTextColor} strokeColor="white" width={14} height={14} />,
            };
        case FailureCode.USER_DEFINED:
            return {
                styleClass: style.userDefinedStatusContent,
                text: t("WorkflowsTable.sidePanel.failure"),
                showDate: true,
                iconComponent: <img src={failedIcon} alt="failed" className={style.warningIcon} />,
            };
        default:
            return {
                styleClass: style.failedStatusContent,
                text: t("WorkflowsTable.sidePanel.configurationErrorFix", {
                    entityType,
                }),
                showDate: true,
                iconComponent: <Warning color={theme.errorTextColor} strokeColor="white" width={14} height={14} />,
            };
    }
}

export function SidePanelLogs(props: SidePanelLogsProps): JSX.Element | null {
    const { t } = useTranslation();
    const theme = useTheme();
    const searchFormRef = React.useRef<HTMLDivElement>(null);
    const skipEffectRef = React.useRef(false);

    const [loading, setLoading] = React.useState(false);
    const [initialLoading, setInitialLoading] = React.useState(true);
    const [search, setSearchQuery] = React.useState("");
    const [dateFilterChanged, setDateFilterChanged] = React.useState(false);
    const [showRunning, setShowRunning] = React.useState(false);
    const [tableState, setTableState] = React.useState<TableState>({
        logs: [],
        cursor: null,
        count: 0,
    });
    const [requestFailureMessage, setRequestFailureMessage] = React.useState("");
    const [showLogs, setShowLogs] = React.useState(true);
    const [dateFilterApplied, setDateFilterApplied] = React.useState(false);
    const [shouldRefetchAfterClear, setShouldRefetchAfterClear] = React.useState(false);
    const [isRefreshing, setIsRefreshing] = React.useState(false);
    const [subworkflowModalOpen, setSubworkflowModalOpen] = React.useState(false);
    const [selectedContextId, setSelectedContextId] = React.useState<string>("");
    const isWorkflowEnabled = props.enabled !== false && props.enabled !== null;

    const canDownloadLogs =
        isCurrentUserInternal() && props.secured
            ? userSessionService.userHasAllAuthorities([
                  props.isTemplate ? AUTH_WORKFLOW_TEMPLATE_SECURED : AUTH_WORKFLOW_SECURED,
              ])
            : !props.secured;

    const fetchData = (initialLoading: boolean, abortController: AbortController, customSearch?: string) => {
        setLoading(true);
        setInitialLoading(initialLoading);

        const state: State = props.filter;
        const report_uuid: string = customSearch !== undefined ? customSearch : search;
        const from: string | undefined =
            props.filterState?.from && props.filterState.from.trim() !== "" ? props.filterState.from : undefined;
        const to: string | undefined =
            props.filterState?.to && props.filterState.to.trim() !== "" ? props.filterState.to : undefined;
        const test_mode: boolean = props.testMode;

        const serviceCall = props.isTemplate
            ? templateService.fetchTemplateExecutionStates(
                  props.workflowUuid,
                  { state, test_mode, from, to, report_uuid },
                  initialLoading ? null : tableState.cursor,
                  abortController
              )
            : workflowService.fetchWorkflowStates(
                  props.workflowUuid,
                  { state, test_mode, from, to, report_uuid },
                  initialLoading ? null : tableState.cursor,
                  abortController
              );

        return serviceCall
            .then((data) => {
                setTableState((prevState) => ({
                    ...prevState,
                    logs: initialLoading
                        ? data.workflowStateDetails
                        : [...prevState.logs, ...data.workflowStateDetails],
                    count: data.count,
                    cursor: data.cursor,
                }));
                setLoading(false);
                setRequestFailureMessage("");
            })
            .catch(() => {
                if (!abortController.signal.aborted) {
                    const entityType = props.isTemplate ? "template" : "workflow";
                    setRequestFailureMessage(t("WorkflowsTable.sidePanel.logsRequestFailureMessage", { entityType }));
                }
            })
            .finally(() => {
                if (!abortController.signal.aborted) {
                    setLoading(false);
                    setInitialLoading(false);
                    setIsRefreshing(false);
                }
            });
    };

    const retryExecution = (contextId: string, abortController: AbortController) => {
        workflowService.retryFailedWorkflowExecution(props.workflowUuid, contextId, abortController).then(() => {
            setShowRunning(false);
        });
    };

    const downloadLogs = (contextId: string) => {
        if (props.isTemplate) {
            templateService
                .fetchTemplateExecutionLogs(props.workflowUuid, contextId, new AbortController())
                .then((result) => {
                    return result;
                });
        } else {
            workflowService.fetchExecutionLogs(props.workflowUuid, contextId, new AbortController()).then((result) => {
                return result;
            });
        }
    };

    function openReportInNewTab(reportUuid: string) {
        window.open(getReportViewUrl(reportUuid), "_blank");
    }

    const hasNewerLogForSameReport = (log: WorkflowStateDetails): boolean => {
        if (!log.reportUuid || !log.completed) {
            return false;
        }
        return filteredLogs.some((otherLog) => {
            if (otherLog.reportUuid !== log.reportUuid || !otherLog.completed) {
                return false;
            }
            const otherCompleted = new Date(otherLog.completed);
            const logCompleted = new Date(log.completed);
            const isNewer = otherCompleted > logCompleted;
            const isNewerOrEqualAndSucceeded = otherCompleted >= logCompleted && otherLog.state === State.SUCCEEDED;
            return isNewer || isNewerOrEqualAndSucceeded;
        });
    };

    const isWorkflowRetryDisabledDueToInactivity = (): boolean =>
        !isWorkflowEnabled && !props.testMode && props.launchType !== LaunchType.MANUAL;

    const getRetryTooltipKey = (log: WorkflowStateDetails): string => {
        if (isWorkflowRetryDisabledDueToInactivity()) {
            return "WorkflowsTable.sidePanel.workflowInactive";
        }
        if (log.failureCode === FailureCode.QUOTA_EXCEEDED) {
            return "WorkflowsTable.sidePanel.quotaLimitReached";
        }
        return "WorkflowsTable.sidePanel.restart";
    };

    const handleStartDateChange = (date: string) => {
        handleFilterChanges({ from: date });
        if (date) {
            setDateFilterChanged(true);
        }
    };

    const handleEndDateChange = (date: string) => {
        handleFilterChanges({ to: date });
        if (date) {
            setDateFilterChanged(true);
        }
    };

    const handleFilterChanges = (update: Partial<FilterState>) => {
        if (props.setFilterState) {
            props.setFilterState((prevState) => ({
                ...prevState,
                ...update,
            }));
        }
    };

    React.useEffect(() => {
        if (skipEffectRef.current) {
            skipEffectRef.current = false;
            return;
        }
        const abortController = new AbortController();
        fetchData(true, abortController);
        return () => {
            abortController.abort();
        };
    }, [props.filter, search, props.workflowUuid]);

    React.useEffect(() => {
        let intervalId: NodeJS.Timeout;

        const shouldAutoRefresh =
            props.autoRefresh &&
            (props.testMode ||
                props.launchType === LaunchType.MANUAL ||
                (props.launchType === LaunchType.REPORT_IMPORT && isWorkflowEnabled));

        if (shouldAutoRefresh) {
            intervalId = setInterval(() => {
                if (tableState.logs.length > 0) {
                    setIsRefreshing(true);
                }

                const abortController = new AbortController();
                fetchData(true, abortController, search);
            }, AUTO_REFRESH_INTERVAL_MS);
        }

        return () => {
            if (intervalId) {
                clearInterval(intervalId);
            }
        };
    }, [props.autoRefresh, props.enabled, search, props.filter]);

    React.useEffect(() => {
        const hideForSearch = props.showSearch && !search;
        const hideForDate = props.showDateFilter && !dateFilterApplied;

        setShowLogs(!hideForSearch && !hideForDate);
    }, [props.showSearch, props.showDateFilter, search, dateFilterApplied]);

    React.useEffect(() => {
        if (shouldRefetchAfterClear && props.filterState?.from === "" && props.filterState?.to === "") {
            setShouldRefetchAfterClear(false);
            const abortController = new AbortController();
            fetchData(true, abortController);
        }
    }, [props.filterState, shouldRefetchAfterClear]);

    const handleManualRefresh = () => {
        if (props.refreshCooldown || props.autoRefresh) {
            return;
        }

        props.setRefreshCooldown(true);
        const abortController = new AbortController();

        if (tableState.logs.length > 0) {
            setIsRefreshing(true);
        }

        fetchData(true, abortController).finally(() => {
            setTimeout(() => {
                props.setRefreshCooldown(false);
            }, REFRESH_COOLDOWN_MS);
        });

        usageStatisticsService.sendEvent({
            label: Label.WORKFLOW_SIDEPANEL,
            action: Action.REFRESH_WORKFLOW_STATES,
            category: Category.WORKFLOW,
        });
    };

    const filteredLogs = tableState.logs;

    const renderScheduledIcon = (scheduled: boolean) =>
        scheduled && (
            <Tooltip content={t("Common.scheduled")}>
                <div
                    className={style.scheduleIcon}
                    data-testid={testIds.workArea.workflows.sidePanel.tabs.log.logItem.scheduledIcon}
                >
                    <ScheduledIcon color="currentColor" />
                </div>
            </Tooltip>
        );

    if (requestFailureMessage) {
        return <div>{requestFailureMessage}</div>;
    }

    const getInfo = (failureCode: FailureCode | null) =>
        getFailureInfo(failureCode, t, theme, props.isTemplate ? "template" : "workflow");

    const getEmptyStateMessage = () => {
        const entityType = props.isTemplate ? "Template" : "Workflow";

        if (props.showSearch && search) {
            return t("WorkflowsTable.sidePanel.noRunsFoundForUuid");
        }

        if (props.showDateFilter && dateFilterApplied) {
            return t("WorkflowsTable.sidePanel.noRunsFoundForDate");
        }

        if (props.filter === State.FAILED) {
            return t("WorkflowsTable.sidePanel.failureEmptyState");
        } else if (props.filter === State.SUCCEEDED) {
            return t("WorkflowsTable.sidePanel.successEmptyState");
        } else {
            return t("WorkflowsTable.sidePanel.allEmptyState", { entityType });
        }
    };

    return (
        <>
            {(props.showSearch && (
                <>
                    <div className={style.searchContainer}>
                        <div className={classNames(formStyle.search)} ref={searchFormRef}>
                            <SearchView
                                setSearch={setSearchQuery}
                                searchInProgress={false}
                                searchWidth={style.searchInput}
                            />
                        </div>
                        <div
                            className={style.searchCheckButton}
                            onClick={() => {
                                const searchInput = searchFormRef.current?.querySelector(
                                    'input[id="searchQuery"]'
                                ) as HTMLInputElement;
                                if (searchInput?.value) {
                                    skipEffectRef.current = true;
                                    const inputValue = searchInput.value;
                                    setSearchQuery(inputValue);

                                    const abortController = new AbortController();
                                    fetchData(true, abortController, inputValue);
                                }
                            }}
                            data-testid={testIds.workArea.workflows.sidePanel.tabs.log.checkSearchFilterButton}
                        >
                            <Checkmark
                                color={theme.contentBackgroundColor}
                                size={CHECKMARK_SIZE}
                                strokeWidth={STROKE_WIDTH}
                            />
                        </div>
                        <div
                            className={style.searchCloseButton}
                            onClick={() => {
                                props.setShowSearch(false);
                                setSearchQuery("");
                            }}
                        >
                            <Exit color={theme.iconFillColor} />
                        </div>
                    </div>
                    {isRefreshing && (
                        <div className={style.runningButton}>
                            <div className={style.rotating}>
                                <RunningIcon color={theme.iconFillColor} />
                            </div>
                            <span className={style.refreshText}>{t("WorkflowsTable.sidePanel.refreshing")}</span>
                        </div>
                    )}
                </>
            )) ||
                (props.showDateFilter && (
                    <>
                        <div className={style.searchContainer}>
                            <div className={classNames(formStyle.search, style.dateInput)}>
                                <SidePanelDatePicker from={handleStartDateChange} to={handleEndDateChange} />
                            </div>
                            <div
                                className={classNames(style.searchCheckButton, {
                                    [style.disabled]: !dateFilterChanged,
                                })}
                                onClick={() => {
                                    if (dateFilterChanged) {
                                        setDateFilterApplied(true);
                                        const abortController = new AbortController();
                                        fetchData(true, abortController);
                                        setDateFilterChanged(false);
                                    }
                                }}
                                data-testid={testIds.workArea.workflows.sidePanel.tabs.log.checkDateFilterButton}
                            >
                                <Checkmark
                                    color={theme.contentBackgroundColor}
                                    size={CHECKMARK_SIZE}
                                    strokeWidth={STROKE_WIDTH}
                                />
                            </div>
                            <div
                                className={style.searchCloseButton}
                                onClick={() => {
                                    props.setShowDateFilter(false);
                                    setDateFilterChanged(false);
                                    setDateFilterApplied(false);
                                    if (props.setFilterState) {
                                        setShouldRefetchAfterClear(true);
                                        props.setFilterState((prevState) => ({
                                            ...prevState,
                                            from: "",
                                            to: "",
                                        }));
                                    }
                                }}
                            >
                                <Exit color={theme.iconFillColor} />
                            </div>
                        </div>
                        {isRefreshing && (
                            <div className={style.runningButton}>
                                <div className={style.rotating}>
                                    <RunningIcon color={theme.iconFillColor} />
                                </div>
                                <span className={style.refreshText}>{t("WorkflowsTable.sidePanel.refreshing")}</span>
                            </div>
                        )}
                    </>
                )) || (
                    <div className={style.headerContainer}>
                        {!isWorkflowEnabled && props.launchType !== LaunchType.MANUAL ? (
                            <div className={style.inactiveBadge}>{t("Common.inactive")}</div>
                        ) : (
                            <Tooltip
                                content={
                                    props.autoRefresh
                                        ? t("WorkflowsTable.sidePanel.autoRefreshOnTooltip")
                                        : props.refreshCooldown
                                          ? t("WorkflowsTable.sidePanel.refreshCooldown")
                                          : t("UpdatePopup.refreshButton")
                                }
                            >
                                <div
                                    onClick={handleManualRefresh}
                                    className={classNames(style.refreshButton, {
                                        [style.disabledIcon]: props.autoRefresh || props.refreshCooldown,
                                    })}
                                    data-testid={testIds.workArea.workflows.sidePanel.tabs.log.refreshButton}
                                >
                                    <RefreshIcon color={theme.iconFillColor} />
                                </div>
                            </Tooltip>
                        )}
                        {(isRefreshing || showRunning) && (
                            <div className={style.runningButton}>
                                <div className={style.rotating}>
                                    <RunningIcon color={theme.iconFillColor} />
                                </div>
                                <span className={style.refreshText}>
                                    {showRunning
                                        ? t("WorkflowsTable.sidePanel.running")
                                        : t("WorkflowsTable.sidePanel.refreshing")}
                                </span>
                            </div>
                        )}
                        <div className={style.autoRefreshContainer}>
                            {props.testMode ||
                            props.launchType === LaunchType.MANUAL ||
                            (props.launchType === LaunchType.REPORT_IMPORT && isWorkflowEnabled) ? (
                                <div
                                    onClick={() => props.setAutoRefresh(!props.autoRefresh)}
                                    className={classNames(style.customToggle, {
                                        [style.toggleActive]: props.autoRefresh,
                                    })}
                                    data-testid={testIds.workArea.workflows.sidePanel.tabs.log.autoRefreshToggle}
                                >
                                    <div className={style.toggleSlider}>
                                        <div className={style.toggleKnob}></div>
                                    </div>
                                </div>
                            ) : (
                                <Tooltip content={t("WorkflowsTable.sidePanel.workflowInactive")}>
                                    <div
                                        className={classNames(style.customToggle, {
                                            [style.toggleActive]: props.autoRefresh,
                                            [style.toggleDisabled]: true,
                                        })}
                                        data-testid={testIds.workArea.workflows.sidePanel.tabs.log.autoRefreshToggle}
                                    >
                                        <div className={style.toggleSlider}>
                                            <div className={style.toggleKnob}></div>
                                        </div>
                                    </div>
                                </Tooltip>
                            )}
                            <span className={style.autoRefreshLabel}>{t("WorkflowsTable.sidePanel.autoRefresh")}</span>
                        </div>
                    </div>
                )}
            <div key={props.filter} className={style.container}>
                {(initialLoading && tableState.logs.length === 0) || (initialLoading && !isRefreshing) ? (
                    <div>
                        <LoadingIndicator />
                    </div>
                ) : (
                    <div>
                        {!showLogs ? (
                            <div className={style.filterMessage}>
                                {props.showSearch
                                    ? t("WorkflowsTable.sidePanel.searchByReportUuid")
                                    : t("WorkflowsTable.sidePanel.searchByDate")}
                            </div>
                        ) : filteredLogs.length > 0 ? (
                            <div className={style.logsList}>
                                {filteredLogs.map((log) => (
                                    <div
                                        key={log.contextId}
                                        className={style.logItem}
                                        data-testid={testIds.workArea.workflows.sidePanel.tabs.log.logItem.itself}
                                    >
                                        <div className={style.statusContainer}>
                                            {log.state === State.SUCCEEDED ? (
                                                <div className={style.successStatusContent}>
                                                    <div className={style.successIcon}>
                                                        <Checkmark color="white" size={7} strokeWidth={2} />
                                                    </div>
                                                    {renderScheduledIcon(log.scheduled)}
                                                    <span>{getStateText(log.state, t)}</span>
                                                    <div className={style.statusDot}></div>
                                                    <span>{formatDate(log.completed)}</span>
                                                </div>
                                            ) : log.state === State.FAILED ? (
                                                (() => {
                                                    const failureInfo = getInfo(log.failureCode);
                                                    return (
                                                        <div className={failureInfo.styleClass}>
                                                            {failureInfo.iconComponent}
                                                            {renderScheduledIcon(log.scheduled)}
                                                            <span>{failureInfo.text}</span>
                                                            {failureInfo.showDate && (
                                                                <>
                                                                    <div className={style.failedStatusDot}></div>
                                                                    <span>{formatDate(log.completed)}</span>
                                                                </>
                                                            )}
                                                        </div>
                                                    );
                                                })()
                                            ) : (
                                                <div>{getStateText(log.state, t)}</div>
                                            )}
                                            <div className={style.iconsContainer}>
                                                {!log.testMode &&
                                                    log.state !== State.SUCCEEDED &&
                                                    !log.parentContextId &&
                                                    (() => {
                                                        const isRetryDisabled =
                                                            hasNewerLogForSameReport(log) ||
                                                            isWorkflowRetryDisabledDueToInactivity() ||
                                                            log.failureCode === FailureCode.QUOTA_EXCEEDED;
                                                        return (
                                                            <Tooltip content={t(getRetryTooltipKey(log))}>
                                                                <div
                                                                    onClick={
                                                                        !isRetryDisabled
                                                                            ? () => {
                                                                                  setShowRunning(true);
                                                                                  retryExecution(
                                                                                      log.contextId,
                                                                                      new AbortController()
                                                                                  );
                                                                                  usageStatisticsService.sendEvent({
                                                                                      label: Label.WORKFLOW_SIDEPANEL,
                                                                                      action: Action.RETRY_WORKFLOW,
                                                                                      category: Category.WORKFLOW,
                                                                                  });
                                                                              }
                                                                            : undefined
                                                                    }
                                                                    className={`${style.icon} ${isRetryDisabled ? style.disabledIcon : ""}`}
                                                                    style={{
                                                                        opacity: isRetryDisabled ? 0.5 : 1,
                                                                        cursor: isRetryDisabled
                                                                            ? "not-allowed"
                                                                            : "pointer",
                                                                    }}
                                                                    data-testid={
                                                                        testIds.workArea.workflows.sidePanel.tabs.log
                                                                            .logItem.retryButton
                                                                    }
                                                                >
                                                                    {" "}
                                                                    <RestartIcon color={theme.iconFillColor} />
                                                                </div>
                                                            </Tooltip>
                                                        );
                                                    })()}
                                                {log.hasSubWorkflowExecutions && (
                                                    <Tooltip
                                                        content={
                                                            log.hasSubWorkflowFailures
                                                                ? t(
                                                                      "WorkflowsTable.sidePanel.tabs.log.failedSubworkflows"
                                                                  )
                                                                : t("WorkflowsTable.sidePanel.tabs.log.subworkflows")
                                                        }
                                                    >
                                                        <div
                                                            className={style.icon}
                                                            onClick={() => {
                                                                setSelectedContextId(log.contextId);
                                                                setSubworkflowModalOpen(true);
                                                            }}
                                                            data-testid={
                                                                testIds.workArea.workflows.sidePanel.tabs.log.logItem
                                                                    .hasSubworkflowButton
                                                            }
                                                        >
                                                            <SubworkflowLogs
                                                                color={theme.iconFillColor}
                                                                error={log.hasSubWorkflowFailures}
                                                            />
                                                        </div>
                                                    </Tooltip>
                                                )}
                                                {canDownloadLogs && (
                                                    <Tooltip content={t("WorkflowsTable.sidePanel.tabs.log.download")}>
                                                        <div
                                                            className={style.icon}
                                                            onClick={() => {
                                                                downloadLogs(log.contextId);
                                                                usageStatisticsService.sendEvent({
                                                                    label: Label.WORKFLOW_SIDEPANEL,
                                                                    action: Action.FETCH_EXECUTION_LOGS,
                                                                    category: Category.WORKFLOW,
                                                                });
                                                            }}
                                                            data-testid={
                                                                testIds.workArea.workflows.sidePanel.tabs.log.logItem
                                                                    .downloadLogsButton
                                                            }
                                                        >
                                                            <DownloadIcon color={theme.iconFillColor} />
                                                        </div>
                                                    </Tooltip>
                                                )}
                                                {log.launchType === LaunchType.REPORT_IMPORT && (
                                                    <Tooltip
                                                        content={t("WorkflowsTable.sidePanel.tabs.log.viewReport")}
                                                    >
                                                        <div
                                                            className={style.icon}
                                                            onClick={() => {
                                                                openReportInNewTab(log.reportUuid);
                                                            }}
                                                            data-testid={
                                                                testIds.workArea.workflows.sidePanel.tabs.log.logItem
                                                                    .viewReportButton
                                                            }
                                                        >
                                                            <Reports color={theme.iconFillColor} />
                                                        </div>
                                                    </Tooltip>
                                                )}
                                            </div>
                                        </div>
                                        <div className={style.started}>
                                            {t("WorkflowsTable.sidePanel.started")} {formatDate(log.started)}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className={style.emptyStateMessage}>{getEmptyStateMessage()}</div>
                        )}
                        <div>
                            {tableState.cursor &&
                                !requestFailureMessage &&
                                showLogs &&
                                filteredLogs.length > 0 &&
                                (loading ? (
                                    <LoadingIndicator small={true} withTable />
                                ) : (
                                    <div
                                        className={classNames(buttons.secondaryButtonWithoutIcon, style.loadMoreButton)}
                                        onClick={() => {
                                            fetchData(false, new AbortController());
                                            usageStatisticsService.sendEvent({
                                                label: Label.WORKFLOW_SIDEPANEL,
                                                action: Action.LOAD_MORE,
                                                category: Category.WORKFLOW,
                                            });
                                        }}
                                        data-testid={testIds.workArea.workflows.sidePanel.tabs.log.loadMoreButton}
                                    >
                                        <div>{t("Common.loadMore")}</div>
                                    </div>
                                ))}
                        </div>
                    </div>
                )}

                {requestFailureMessage && <div>{requestFailureMessage}</div>}
            </div>
            {subworkflowModalOpen && (
                <SubworkflowLogsModal
                    isOpen={subworkflowModalOpen}
                    onClose={() => setSubworkflowModalOpen(false)}
                    workflowUuid={props.workflowUuid}
                    contextId={selectedContextId}
                    entityType={props.isTemplate ? WorkflowEntityType.TEMPLATE : WorkflowEntityType.WORKFLOW}
                    secured={props.secured}
                />
            )}
        </>
    );
}

function getStateText(state: State, t: (key: string, options?: TranslationOptions) => string) {
    switch (state) {
        case State.SUCCEEDED:
            return t("WorkflowsTable.sidePanel.success");
        case State.FAILED:
            return t("WorkflowsTable.sidePanel.failure");
        default:
            return "";
    }
}
